import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
// eslint-disable-next-line no-duplicate-imports
import type { tenantType, TrackEventPayload } from '@atlassiansox/analytics-web-client';

import type { GasPurePayload, GasPureScreenEventPayload } from '@atlaskit/analytics-gas-types';
import FabricAnalyticsListeners, {
	type AnalyticsWebClient as AnalyticsWebClientListener,
} from '@atlaskit/analytics-listeners';
import { AnalyticsListener } from '@atlaskit/analytics-next';
import {
	ANALYTICS_BRIDGE_CHANNEL,
	extractAWCDataFromEvent,
	OPERATIONAL_EVENT_TYPE,
	SCREEN_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	UI_EVENT_TYPE,
} from '@atlassian/analytics-bridge';
import { initialiseErrorAnalyticsClient } from '@atlassian/error-handling';

type CustomAttributes = { [key: string]: any };

interface AnalyticsContextValue {
	analyticsClient: AnalyticsWebClient;
	customAttributes: CustomAttributes;
	updateCustomAttributes: (key: string, value: any) => void;
}

export const AnalyticsContext: React.Context<AnalyticsContextValue | null> =
	createContext<AnalyticsContextValue | null>(null);

/**
 * An adapter layer to translate between "@atlaskit/analytics-listeners" and
 * "@atlassiansox/analytics-web-client" due to discrepancy between the defined types
 */
const useAtlasKitAnalytics = (
	analyticsWebClient: AnalyticsWebClient,
): AnalyticsWebClientListener => {
	return useMemo(
		() => ({
			sendUIEvent: (event: GasPurePayload) => {
				return analyticsWebClient.sendUIEvent({
					...event,
					action: event.action || '',
					source: event.source || 'atlassian-studio',
				});
			},
			sendOperationalEvent: (event: GasPurePayload) => {
				return analyticsWebClient.sendOperationalEvent({
					...event,
					action: event.action || '',
					source: event.source || 'atlassian-studio',
				});
			},
			sendTrackEvent: (event: GasPurePayload) => {
				return analyticsWebClient.sendTrackEvent({
					...event,
					action: event.action || '',
					source: event.source || 'atlassian-studio',
				});
			},
			sendScreenEvent: (event: GasPureScreenEventPayload) => {
				return analyticsWebClient.sendScreenEvent(event);
			},
		}),
		[analyticsWebClient],
	);
};

export type AnalyticsProviderProps = {
	analyticsClient: AnalyticsWebClient;
	children: React.ReactNode;
};

export const AnalyticsProvider = ({
	children,
	analyticsClient,
}: AnalyticsProviderProps): React.JSX.Element => {
	const [customAttributes, setCustomAttributes] = useState<CustomAttributes>({});

	const updateCustomAttributes = useCallback((key: string, value: any) => {
		setCustomAttributes((prevConfig) => ({ ...prevConfig, [key]: value }));
	}, []);

	const sendEvent = useCallback(
		(event: any) => {
			const { type, payload } = event;

			switch (type) {
				case UI_EVENT_TYPE:
					analyticsClient.sendUIEvent(payload);
					break;
				case TRACK_EVENT_TYPE:
					analyticsClient.sendTrackEvent(payload);
					break;
				case OPERATIONAL_EVENT_TYPE:
					analyticsClient.sendOperationalEvent(payload);
					break;
				case SCREEN_EVENT_TYPE:
					analyticsClient.sendScreenEvent(payload.name, null, payload.attributes);
					break;
				default:
					break;
			}
		},
		[analyticsClient],
	);

	useEffect(() => {
		initialiseErrorAnalyticsClient({
			sendOperationalEvent: (event: any) => {
				analyticsClient.sendOperationalEvent({
					action: event.action || 'unknown',
					actionSubject: event.actionSubject || 'error',
					source: event.source || 'atlassian-studio',
					...event,
				});
			},
		});
	}, [analyticsClient]);

	return (
		<AnalyticsContext.Provider
			value={{
				analyticsClient,
				customAttributes,
				updateCustomAttributes,
			}}
		>
			<AnalyticsListener
				channel={ANALYTICS_BRIDGE_CHANNEL}
				onEvent={(event) => sendEvent(extractAWCDataFromEvent(event))}
			>
				<FabricAnalyticsListeners client={useAtlasKitAnalytics(analyticsClient)}>
					{children}
				</FabricAnalyticsListeners>
			</AnalyticsListener>
		</AnalyticsContext.Provider>
	);
};

export const useAnalytics = (): {
	customAttributes: CustomAttributes;
	sendTrackEvent: (event: TrackEventPayload, callback?: any) => Promise<void>;
	setTenantInfo: (tenantIdType: tenantType, tenantId?: string) => void;
	setUserInfo: (userIdType: string, userId: string) => void;
	updateCustomAttributes: (key: string, value: any) => void;
} => {
	const context = useContext(AnalyticsContext);

	if (!context) {
		throw new Error('useAnalytics must be used within an AnalyticsProvider');
	}

	const { customAttributes, updateCustomAttributes, analyticsClient } = context;

	const { setTenantInfo, setUserInfo, sendTrackEvent } = analyticsClient;

	return {
		setTenantInfo,
		setUserInfo,
		customAttributes,
		updateCustomAttributes,
		sendTrackEvent,
	};
};
